const DYNAMIC_RULE_TYPES = ['Value', 'MLRule', 'Cyclic', 'ETL']

const USER_PARAM_OPTIONS = [
  { key: 'country', label: 'Country', mode: 'range' },
  { key: 'not_in_country', label: 'Not In Country', mode: 'range' },
  { key: 'language', label: 'Language', mode: 'range' },
  { key: 'timezone', label: 'Timezone', mode: 'range' },
  { key: 'campaign', label: 'Campaign', mode: 'single' },
  { key: 'lifetime', label: 'Lifetime', mode: 'range' },
  { key: 'rv_total', label: 'RV total', mode: 'range' },
  { key: 'rv_day', label: 'RV last day', mode: 'range' },
  { key: 'level', label: 'Level', mode: 'range' },
  { key: 'payable', label: 'Payer', mode: 'single' },
  {
    key: 'payer_segmentation',
    label: 'Payer segmentation',
    mode: 'select_single',
    values: [
      { value: 'non_payer', label: 'non_payer' },
      { value: 'small_payer', label: 'small_payer' },
      { value: 'mid_payer', label: 'mid_payer' },
      { value: 'high_payer', label: 'high_payer' },
      { value: 'dormant_small', label: 'dormant_small' },
      { value: 'dormant_high', label: 'dormant_high' },
    ],
  },
  { key: 'traffic_type', label: 'Traffic Type', mode: 'range' },
  { key: 'adtag', label: 'Ad tag', mode: 'single' },
  { key: 'user_purchases', label: 'SKU id', mode: 'single' },
]

const USER_PARAM_BY_KEY = Object.fromEntries(USER_PARAM_OPTIONS.map((item) => [item.key, item]))

function makeElementId() {
  return `dre_${Date.now()}_${Math.random().toString(16).slice(2)}`
}

export function createDynamicRuleElement() {
  return {
    id: makeElementId(),
    leftBoundary: '',
    rightBoundary: '',
    output: '',
  }
}

export function getDynamicRuleTypes() {
  return DYNAMIC_RULE_TYPES
}

export function getUserParamOptions() {
  return USER_PARAM_OPTIONS
}

export function getUserParamByKey(key) {
  return USER_PARAM_BY_KEY[String(key || '').trim()] || null
}

export function isUserParamRequired(ruleType) {
  return ruleType !== 'Cyclic'
}

export function getDynamicRuleElementMode(ruleType, userParam) {
  if (ruleType === 'MLRule') return 'ml'
  if (ruleType === 'Cyclic') return 'cyclic'
  if (ruleType === 'ETL') return 'single'
  const option = getUserParamByKey(userParam)
  if (!option) return 'range'
  return option.mode || 'range'
}

export function validateDynamicRuleDraft({ ruleType, userParam, elements, defaultOutput }) {
  if (isUserParamRequired(ruleType) && !String(userParam || '').trim()) {
    return 'User param is required for this rule type.'
  }

  const rows = Array.isArray(elements) ? elements : []
  if (rows.length === 0) {
    return 'Add at least one dynamic rule element.'
  }

  const mode = getDynamicRuleElementMode(ruleType, userParam)
  for (const row of rows) {
    const left = String(row.leftBoundary || '').trim()
    const right = String(row.rightBoundary || '').trim()
    const output = String(row.output || '').trim()

    if (mode === 'ml') {
      if (!left) return 'Fill all ML model names.'
      continue
    }
    if (mode === 'cyclic') {
      if (!left || !right || !output) return 'Fill order, duration and output in all rows.'
      continue
    }
    if (mode === 'single' || mode === 'select_single') {
      if (!left || !output) return 'Fill condition and output in all rows.'
      continue
    }
    if (!left || !right || !output) return 'Fill from, to and output in all rows.'
  }

  if (mode !== 'ml' && mode !== 'cyclic' && !String(defaultOutput || '').trim()) {
    return 'Default output is required.'
  }

  return null
}

export function buildDynamicRuleElementsPayload({ ruleType, userParam, elements, defaultOutput }) {
  const mode = getDynamicRuleElementMode(ruleType, userParam)
  const rows = Array.isArray(elements) ? elements : []
  const payload = []

  for (const row of rows) {
    const left = String(row.leftBoundary || '').trim()
    const right = String(row.rightBoundary || '').trim()
    const output = String(row.output || '').trim()

    if (mode === 'ml') {
      if (!left) continue
      payload.push({
        left_boundary: left,
        right_boundary: '',
        output: '',
        is_default: false,
      })
      continue
    }

    if (mode === 'cyclic') {
      if (!left || !right || !output) continue
      payload.push({
        left_boundary: left,
        right_boundary: right,
        output,
        is_default: false,
      })
      continue
    }

    if (mode === 'single' || mode === 'select_single') {
      if (!left || !output) continue
      payload.push({
        left_boundary: left,
        right_boundary: left,
        output,
        is_default: false,
      })
      continue
    }

    if (!left || !right || !output) continue
    payload.push({
      left_boundary: left,
      right_boundary: right,
      output,
      is_default: false,
    })
  }

  const normalizedDefault = String(defaultOutput || '').trim()
  if (mode !== 'ml' && mode !== 'cyclic' && normalizedDefault) {
    payload.push({
      left_boundary: 'default',
      right_boundary: 'default',
      output: normalizedDefault,
      is_default: true,
    })
  }

  return payload
}
